import { Inject, Injectable } from '@angular/core';
import { CognitoJwtVerifier } from 'aws-jwt-verify';
import { CognitoJwtPayload } from 'aws-jwt-verify/jwt-model';
import { Auth } from 'aws-amplify';
import {
  AmplifyFederatedInfo,
  IAuthenticatedUser,
  ITokenService,
  IdTokenPayload,
} from './auth.type';
import { AuthConfig } from './auth.config';
import {
  DEFAULT_USER_AMILEARN_STATUS,
  UserAmilearnStatus,
} from '../user/user.type';
import { NGXLogger } from 'ngx-logger';
import { StorageService } from '../local-storage/storage.service';
import { STORAGE_TOKEN, StorageKey } from '../local-storage/storage.type';

@Injectable({
  providedIn: 'root',
})
export class TokenService implements ITokenService {
  private cachedIdTokenPayload: CognitoJwtPayload;
  private cachedPayload: IdTokenPayload;
  private storageService: StorageService;

  constructor(
    private authConfig: AuthConfig,
    private logger: NGXLogger,
    @Inject(STORAGE_TOKEN) localStorageService: StorageService
  ) {
    this.storageService = localStorageService;
  }

  setUserStatus(status: string): void {
    this.logger.debug('[TokenService] setUserStatus - new status: ', status);

    this.cachedPayload = {
      ...this.cachedPayload,
      amilearnStatus: this.userAmilearnStatus(status),
    };
  }

  idTokenPayload(): IdTokenPayload {
    this.logger.debug(
      '[TokenService] idTokenPayload - returning cached payload:',
      this.cachedPayload
    );
    return this.cachedPayload;
  }

  async currentAuthenticatedUser(): Promise<IAuthenticatedUser | null> {
    this.logger.info('[TokenService] currentAuthenticatedUser() - starting');

    await Auth.currentCredentials();
    const user = (await Auth.currentAuthenticatedUser({
      bypassCache: true,
    })) as IAuthenticatedUser;

    if (user) {
      this.logger.debug(
        '[TokenService] currentAuthenticatedUser() - user is non-null'
      );

      if (!this.cachedIdTokenPayload) {
        this.logger.debug(
          '[TokenService] currentAuthenticatedUser() - cachedIdTokenPayload is null. Refreshing cached payloads'
        );

        await this.refreshCachedPayloads(user);
      } else {
        // this.cachedIdTokenPayload exists
        this.logger.debug(
          '[TokenService] currentAuthenticatedUser() - cachedIdTokenPayload is non-null'
        );
        this.logger.debug(
          '[TokenService] currentAuthenticatedUser() - loading payload from user.token'
        );

        const userTokenPayload = await this.loadIdTokenPayload(user.token);
        if (!userTokenPayload) {
          this.logger.debug(
            '[TokenService] currentAuthenticatedUser() - userTokenPayload is null. Cleaning up'
          );

          this.cleanCachedPayloads();
          return null;
        }

        if (userTokenPayload.jti !== this.cachedIdTokenPayload.jti) {
          this.logger.debug(
            '[TokenService] currentAuthenticatedUser() - userTokenPayload.jti != this.cachedIdTokenPayload.jti - Refreshing cached payloads'
          );

          await this.refreshCachedPayloads(user);
        }
      }
    } else {
      this.logger.debug(
        '[TokenService] currentAuthenticatedUser() - user is null. Cleaning up'
      );

      this.cleanCachedPayloads();
    }

    this.logger.debug(
      '[TokenService] currentAuthenticatedUser() - returning user',
      user
    );
    return user;
  }

  storedAuthenticatedUser(): IAuthenticatedUser | null {
    const federatedInfo = this.storageService.getItem<AmplifyFederatedInfo>(
      StorageKey.AMPLIFY_FEDERATED_INFO
    );
    if (!federatedInfo) {
      return null;
    }
    return {
      id: federatedInfo.user.id,
      name: federatedInfo.user.name,
      token: federatedInfo.token,
    };
  }

  removeStoredAuthenticatedUser(): void {
    this.storageService.removeItem(StorageKey.AMPLIFY_FEDERATED_INFO);
  }

  cleanCachedPayloads(): void {
    this.logger.debug('[TokenService] cleanCachedPayloads()');
    this.cachedPayload = null;
    this.cachedIdTokenPayload = null;
  }

  async refreshCachedPayloads(user: IAuthenticatedUser): Promise<void> {
    this.logger.debug(
      '[TokenService] refreshCachedPayloads() - deleting cached payload'
    );

    this.cleanCachedPayloads();
    this.cachedIdTokenPayload = await this.loadIdTokenPayload(user.token);

    if (!this.cachedIdTokenPayload) {
      this.logger.warn(
        '[TokenService] refreshCachedPayloads() - failed to load id token Payload'
      );
      return;
    }

    this.logger.debug(
      `[TokenService] refreshCachedPayloads() - resetting cachedPayload to FRESH token Payload amilearnStatus: ${this.amilearnStatus(
        this.cachedIdTokenPayload
      )}`
    );

    this.cachedPayload = {
      email: this.cachedIdTokenPayload.email as string,
      country: this.cachedIdTokenPayload['custom:ms-regionid'] as string,
      amilearnStatus: this.amilearnStatus(this.cachedIdTokenPayload),
      userOrigin: this.cachedIdTokenPayload['custom:ms-id'] ? 'MSAW' : 'WWW',
    };
  }

  async loadIdTokenPayload(
    jwtIdToken: string
  ): Promise<CognitoJwtPayload | null> {
    this.logger.debug('[TokenService] loadIdTokenPayload started');

    const tokenUse = 'id';
    const { userPoolId, userPoolWebClientId } = this.authConfig.awsAmplify.Auth;
    const verifier = CognitoJwtVerifier.create({
      userPoolId,
      tokenUse,
      clientId: userPoolWebClientId,
    });

    try {
      const payload = await verifier.verify(jwtIdToken);
      this.logger.debug(
        `[TokenService] loadIdTokenPayload - '${tokenUse}' Token is VALID. Payload:`,
        payload
      );
      return payload;
    } catch (err) {
      this.logger.error(
        `[TokenService] loadIdTokenPayload - '${tokenUse}' Token NOT VALID!:`,
        err
      );
      return null;
    }
  }

  // UTILITY
  userAmilearnStatus(customAmilearnStatus: string): UserAmilearnStatus {
    const status = UserAmilearnStatus[customAmilearnStatus.toUpperCase()];

    return status || DEFAULT_USER_AMILEARN_STATUS;
  }

  amilearnStatus(payload: CognitoJwtPayload): UserAmilearnStatus {
    const payloadAmilearnStatus =
      payload && (payload['custom:amilearn-status'] as string);

    if (!payloadAmilearnStatus) {
      return DEFAULT_USER_AMILEARN_STATUS;
    } else {
      return (
        this.userAmilearnStatus(payloadAmilearnStatus) ||
        DEFAULT_USER_AMILEARN_STATUS
      );
    }
  }

  test_setPrivateFields(
    cachedIdTokenPayload: CognitoJwtPayload,
    cachedPayload: IdTokenPayload
  ): void {
    this.cachedIdTokenPayload = cachedIdTokenPayload;
    this.cachedPayload = cachedPayload;
  }

  test_getPrivateFields(): {
    cachedIdTokenPayload: CognitoJwtPayload,
    cachedPayload: IdTokenPayload
  } {
    return {
      cachedIdTokenPayload: this.cachedIdTokenPayload,
      cachedPayload: this.cachedPayload,
    };
  }
}
